import { useEffect, useRef, useState, type ReactNode } from "react";

/** Fades + rises its children into view the first time they enter the
 *  viewport. Observes against the .landing-root scroll container. Honors
 *  prefers-reduced-motion (shows immediately). */
export function Reveal({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    let io: IntersectionObserver | null = null;
    try {
      const root = el.closest(".landing-root");
      io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              setVisible(true);
              io?.disconnect();
            }
          }
        },
        { root: root ?? null, threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
      );
      io.observe(el);
    } catch {
      // If the observer can't be created, reveal rather than hide the content.
      setVisible(true);
    }

    return () => io?.disconnect();
  }, []);

  return (
    <div ref={ref} className={`reveal${visible ? " is-visible" : ""}`}>
      {children}
    </div>
  );
}
